import { ControlOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Input } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { SortKey } from '../../utils/sort'
import TeamListSortDropdown from './TeamListSortDropdown'

type TeamListToolbarProps = {
  searchValue: string
  onSearchChange: (value: string) => void
  searchVisible?: boolean
  onFiltersClick?: () => void
  // Custom filter control (popover / side-panel trigger) rendered in place of
  // the default Filters button. Each screen injects its own surface (CM-3581).
  filtersControl?: React.ReactNode
  sortKey: SortKey
  onSortChange: (value: SortKey) => void
  testId?: string
}

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  min-height: 56px;
  background-color: #fafafa;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-wrap: wrap;
  }
`

const StyledSearchInput = styled(Input)`
  width: 256px;
  height: 40px;
  border-radius: 6px !important;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
`

const StyledRightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-left: 0;
    width: 100%;
  }
`

const SEARCH_DEBOUNCE_MS = 150

const TeamListToolbar: React.FC<TeamListToolbarProps> = ({
  searchValue,
  onSearchChange,
  searchVisible = true,
  onFiltersClick,
  filtersControl,
  sortKey,
  onSortChange,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const [draft, setDraft] = useState(searchValue)

  useEffect(() => setDraft(searchValue), [searchValue])

  useEffect(() => {
    if (draft === searchValue) {
      return
    }
    const handle = setTimeout(() => onSearchChange(draft), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [draft, searchValue, onSearchChange])

  return (
    <StyledRow data-testid={testId}>
      {searchVisible && (
        <StyledSearchInput
          id="team-list-search"
          aria-label={t(reportsConstants.TEAM_LIST_SEARCH_ARIA)}
          placeholder={t(reportsConstants.TEAM_LIST_SEARCH_PLACEHOLDER)}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          suffix={<SearchOutlined />}
          data-testid={testId ? `${testId}-search` : undefined}
        />
      )}
      <StyledRightControls>
        {filtersControl ?? (
          <Button
            variant="default"
            icon={<ControlOutlined />}
            onClick={onFiltersClick}
            data-testid={testId ? `${testId}-filters` : undefined}
          >
            {t(reportsConstants.TEAM_LIST_FILTERS)}
          </Button>
        )}
        <TeamListSortDropdown
          value={sortKey}
          onChange={onSortChange}
          testId={testId ? `${testId}-sort` : undefined}
        />
      </StyledRightControls>
    </StyledRow>
  )
}

export default TeamListToolbar
